import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Radio from '@mui/material/Radio';
import { makeStyles } from '@mui/styles';

import { Provider } from '@apps/shared/src/providerSearchAdmin/types/providerSearch';
import {
  fetchProviderData,
  setActionInitiated,
  setSelectedProviderId,
  updateProviderSearch,
} from '@apps/shared/src/providerSearchAdmin/actions';
import VirtualizedTable, { ColumnData, Data } from './VirtualizedTable';
import UpdateProviderSearch from './UpdateProviderSearch';
import formatDate from '../shared/date';
import { RootState } from '../store';

const useStyles = makeStyles({
  container: {
    padding: '0 1rem',
    boxSizing: 'border-box',
  },
  email: {
    wordBreak: 'break-word',
  },
});

type ParentProps = {
  providerList?: Provider[];
};
type StateProps = {
  actionInitiated: string;
  selectedProviderID: string;
  selectedProvider: Provider | null;
  hasError: boolean;
  isActionPending: boolean;
  searchInput: string;
  sortBy: string;
  entityType: string;
  recommendation: string;
};
type DispatchProps = {
  setSelectedProviderId: (id: string) => void;
  updateProviderSearch: (formData: any) => void;
};

type Props = ParentProps & StateProps & DispatchProps;

const VirtualizedProviderList: React.FC<Props> = ({
  providerList,
  actionInitiated,
  selectedProviderID,
  selectedProvider,
  hasError,
  isActionPending,
  searchInput,
  sortBy,
  recommendation,
  entityType,
  setSelectedProviderId,
  updateProviderSearch,
}) => {
  const classes = useStyles();
  const [selectedRow, setSelectedRow] = useState<number | null>(null);

  useEffect(() => {
    if (actionInitiated !== 'update') {
      setSelectedRow(null); // Clear selection if not in update mode
    }
    return () => {
      setSelectedProviderId('');
      setActionInitiated('');
    };
  }, [actionInitiated]);

  const columns: ColumnData[] = [
    { dataKey: 'selection', label: '', width: 50 },
    { dataKey: 'name', label: 'Provider Name', width: 200 },
    { dataKey: 'npi', label: 'NPI', width: 150 },
    { dataKey: 'taxID', label: 'Tax ID', width: 150 },
    { dataKey: 'entityType', label: 'Entity Type', width: 150 },
    { dataKey: 'recommendation', label: 'Recommendation', width: 150 },
    { dataKey: 'externalNotes', label: 'External Notes', width: 200 },
    { dataKey: 'internalNotes', label: 'Internal Notes', width: 200 },
    { dataKey: 'active', label: 'Active', width: 100 },
    { dataKey: 'lastUpdateDate', label: 'Last Update Date', width: 150 },
  ];

  const transformData = (provider: Provider, index: number): Data => {
    const rowData: Data = {
      name: provider.providerName,
      npi: provider.npi,
      taxID: provider.ein.trim() ? provider.ein : 'NULL',
      entityType: provider.entityType,
      recommendation: provider.recommendation ? provider.recommendation.toUpperCase() : 'NULL',
      externalNotes: provider.external_notes ? provider.external_notes : 'NULL',
      internalNotes: provider.internal_notes ? provider.internal_notes : 'NULL',
      active: provider.active ? provider.active.toString() : 'false',
      lastUpdateDate: provider.updatedDate ? formatDate(provider.updatedDate) : 'NULL',
    };

    if (actionInitiated === 'update') {
      rowData.selection = (
        <Radio
          checked={selectedRow === index}
          onChange={() => handleRadioChange(index, provider.npi)}
          color="primary"
        />
      );
    }

    return rowData;
  };

  const handleRadioChange = (index: number, npi: string) => {
    setSelectedRow(index);
    setSelectedProviderId(npi);
  };

  const handleCloseUpdatePopup = () => {
    setSelectedProviderId('');
    setSelectedRow(null);
  };

  const handleUpdateData = (formData: any) => {
    updateProviderSearch(formData);
    fetchProviderData(1, 25, searchInput, sortBy, recommendation, entityType);
  };

  return (
    <>
      <div className={classes.container}>
        <VirtualizedTable
          columns={columns}
          data={providerList ? providerList.map(transformData) : []}
        />
      </div>
      <UpdateProviderSearch
        open={selectedProviderID !== ''}
        actionInitiated={actionInitiated}
        onClose={handleCloseUpdatePopup}
        onUpdate={handleUpdateData}
        providerData={selectedProvider}
        hasError={hasError}
        isActionPending={isActionPending}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const {
    actionInitiated,
    selectedProviderID,
    providers,
    hasError,
    isActionPending,
    searchInput,
    sortBy,
    recommendation,
    entityType,
  } = state.providerSearchAdmin;
  const selectedProvider =
    (providers && providers.find((p: Provider) => p.npi === selectedProviderID)) || [];

  return {
    actionInitiated,
    selectedProviderID,
    selectedProvider,
    hasError,
    isActionPending,
    searchInput,
    sortBy,
    recommendation,
    entityType,
  };
};

const mapDispatchToProps = {
  setSelectedProviderId,
  updateProviderSearch,
};

export default connect(mapStateToProps, mapDispatchToProps)(VirtualizedProviderList);
